import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    CircularProgress,
    makeStyles,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    FormControlLabel,
    Checkbox,
    Menu,
    MenuItem,
    Button,
    Popover,
    useTheme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import BusinessIcon from '@material-ui/icons/Business'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import GetAppIcon from '@material-ui/icons/GetApp'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import { toast } from 'react-toastify'
import {
    useAdminFilterOrganizations,
    useAdminVendorsList,
    buildAdminVendorsFilterParams,
    useVendors,
} from '../hooks'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../../constants'
import { HeadCell, Selector } from '../../../components'
import { Company, CompanyStatus, PlanStatus } from '../../../models'
import { CompanyWithInvoiceCode } from '../Property/types'
import { VendorApartmentModal } from './VendorApartmentModal'
import {
    VendorView,
    VendorViewPills,
} from '../../VendorPerformance/VendorViewPills'
import { AdminVendorAnalytics } from './AdminVendorAnalytics'
import { useDebounce } from '../../../hooks/useDebounce'
import { axiosInstance, statesList } from '../../../helpers'
import {
    EDITABLE_STATUS_OPTIONS,
    formatStatusLabel,
    getStatusDotColor,
} from '../Property/utils/statusUtils'

const TABLE_HEAD_CELLS: HeadCell<{
    id: string
    name: string
    invoice_code: string
    email: string
    in_house: string
    address: string
    property_count: string
    actions: string
}>[] = [
    { id: 'id', label: 'ID', align: 'left', disablePadding: false, width: 70 },
    {
        id: 'name',
        label: 'Name',
        align: 'left',
        disablePadding: false,
        width: 250,
    },
    {
        id: 'invoice_code',
        label: 'Invoice Code',
        align: 'left',
        disablePadding: false,
        width: 120,
    },
    {
        id: 'email',
        label: 'Email',
        align: 'left',
        disablePadding: false,
        width: 200,
    },
    {
        id: 'in_house',
        label: 'Type',
        align: 'left',
        disablePadding: false,
        width: 100,
    },
    {
        id: 'address',
        label: 'Address',
        align: 'left',
        disablePadding: false,
        width: 250,
    },
    {
        id: 'property_count',
        label: 'Property Count',
        align: 'center',
        disablePadding: false,
        width: 120,
    },
    {
        id: 'actions',
        label: 'Actions',
        align: 'center',
        disablePadding: false,
        width: 100,
    },
]

const VENDOR_SORT_KEY_TO_ORDERING: Record<string, string> = {
    id: 'id',
    organization: 'organization',
    name: 'name',
    invoice_code: 'invoice_code',
    email: 'email',
    in_house: 'in_house',
    address: 'address',
    property_count: 'property_count',
}

const toggleOrdering = (currentOrdering: string, sortKey: string): string => {
    const base = VENDOR_SORT_KEY_TO_ORDERING[sortKey] ?? sortKey
    if (currentOrdering === base) {
        return `-${base}`
    }
    return base
}

const STATE_SELECTOR_DATA = statesList.map((code, index) => ({
    id: index,
    name: code,
}))

const TYPE_SELECTOR_DATA = [
    { id: 0, name: 'In House', inHouse: true },
    { id: 1, name: 'Contractor', inHouse: false },
]

type StatusSelectorItem = {
    id: number
    name: string
    status?: CompanyStatus
}

const STATUS_SELECTOR_DATA: StatusSelectorItem[] = EDITABLE_STATUS_OPTIONS.map(
    (statusOption, index) => ({
        id: index,
        name: formatStatusLabel(statusOption),
        status: statusOption,
    }),
)

const StatusOptionLabel = ({
    status,
    label,
}: {
    status?: CompanyStatus
    label: string
}) => {
    const theme = useTheme()

    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing(1),
                lineHeight: 1,
            }}
        >
            {status && (
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: getStatusDotColor(status, theme),
                        flexShrink: 0,
                    }}
                />
            )}
            <span style={{ lineHeight: 1 }}>{label}</span>
        </span>
    )
}

type StatusPopoverContext = {
    vendorId: number
    currentStatus: CompanyStatus
    propertyCount: number | null
}

type StatusPopoverState = {
    anchorPosition: { top: number; left: number }
    context: StatusPopoverContext
}

const StatusChangePopover = ({
    popoverState,
    onClose,
    onStatusChange,
}: {
    popoverState: StatusPopoverState | null
    onClose: () => void
    onStatusChange: (id: number, newStatus: CompanyStatus) => void
}) => {
    const theme = useTheme()
    const context = popoverState?.context ?? null

    return (
        <Popover
            open={popoverState !== null}
            anchorReference="anchorPosition"
            anchorPosition={popoverState?.anchorPosition}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <div style={{ padding: theme.spacing(1, 0), minWidth: 160 }}>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{
                        display: 'block',
                        padding: theme.spacing(0, 2, 0.5),
                    }}
                >
                    Change status
                </Typography>
                {EDITABLE_STATUS_OPTIONS.map((option) => {
                    const deactivateBlocked =
                        option === CompanyStatus.IN_ACTIVE &&
                        context != null &&
                        context.propertyCount !== 0 &&
                        option !== context.currentStatus

                    return (
                        <Tooltip
                            key={option}
                            title={
                                deactivateBlocked
                                    ? 'Vendor must have zero associated properties'
                                    : ''
                            }
                        >
                            <span>
                                <MenuItem
                                    selected={option === context?.currentStatus}
                                    disabled={deactivateBlocked}
                                    onClick={() => {
                                        if (
                                            context &&
                                            option !== context.currentStatus &&
                                            !deactivateBlocked
                                        ) {
                                            onStatusChange(
                                                context.vendorId,
                                                option,
                                            )
                                        }
                                        onClose()
                                    }}
                                >
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: theme.spacing(1),
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: getStatusDotColor(
                                                    option,
                                                    theme,
                                                ),
                                                flexShrink: 0,
                                            }}
                                        />
                                        {formatStatusLabel(option)}
                                    </span>
                                </MenuItem>
                            </span>
                        </Tooltip>
                    )
                })}
            </div>
        </Popover>
    )
}

const useLocalStyles = makeStyles((theme) => ({
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        maxHeight: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        padding: theme.spacing(1.5, 2, 2),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
    searchField: {
        flex: 1,
        minWidth: 220,
    },
    downloadButton: {
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    tableWrapper: {
        flex: 1,
        display: 'flex',
        overflow: 'scroll',
        flexDirection: 'column',
        position: 'relative',
    },
    loadingOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    table: {
        minWidth: 750,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        minHeight: 52,
        maxHeight: 52,
    },
    sortableHeader: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    sortIcon: {
        marginLeft: theme.spacing(0.5),
        fontSize: '1rem',
    },
    actionButton: {
        padding: theme.spacing(0.5),
    },
    actionsCell: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
    },
}))

const VendorStatusDot = ({ status }: { status: CompanyStatus }) => {
    const theme = useTheme()
    const color = getStatusDotColor(status, theme)
    const label = formatStatusLabel(status)

    return (
        <Tooltip title={`Status: ${label}`}>
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: theme.spacing(0.25),
                }}
            >
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: color,
                        flexShrink: 0,
                    }}
                />
            </span>
        </Tooltip>
    )
}

const VendorNameCell = ({
    name,
    organization,
    status,
    isPremium,
    onNameClick,
}: {
    name: string
    organization: string
    status: CompanyStatus
    isPremium: boolean
    onNameClick?: (event: React.MouseEvent) => void
}) => {
    const theme = useTheme()

    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
            }}
        >
            <VendorStatusDot status={status} />
            <div>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        onClick={onNameClick}
                        style={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            cursor: onNameClick ? 'pointer' : 'default',
                            color: onNameClick
                                ? theme.palette.primary.main
                                : undefined,
                        }}
                    >
                        {name}
                    </Typography>
                    {isPremium && (
                        <Typography
                            component="span"
                            variant="body2"
                            color="primary"
                        >
                            (Premium)
                        </Typography>
                    )}
                </span>
                <Typography variant="subtitle2" color="textSecondary">
                    {organization}
                </Typography>
            </div>
        </span>
    )
}

export const VendorList = () => {
    const localClasses = useLocalStyles()
    const theme = useTheme()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [searchText, setSearchText] = useState('')
    const debouncedSearch = useDebounce(searchText, 400)
    const [status, setStatus] = useState<CompanyStatus | 'ALL'>(
        CompanyStatus.ACTIVE,
    )
    const [stateFilter, setStateFilter] = useState<string | null>(null)
    const [organizationId, setOrganizationId] = useState<number | null>(null)
    const [selectedView, setSelectedView] = useState<VendorView>('directory')
    const [typeFilter, setTypeFilter] = useState<boolean | null>(null)
    const [premiumOnly, setPremiumOnly] = useState(false)
    const [ordering, setOrdering] = useState('-id')
    const [isExporting, setIsExporting] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [
        selectedVendor,
        setSelectedVendor,
    ] = useState<CompanyWithInvoiceCode | null>(null)
    const [vendorApartments, setVendorApartments] = useState<Company[]>([])
    const [apartmentsLoading, setApartmentsLoading] = useState(false)
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const [menuVendor, setMenuVendor] = useState<CompanyWithInvoiceCode | null>(
        null,
    )
    const [actionsLoading, setActionsLoading] = useState(false)
    const [
        statusPopoverState,
        setStatusPopoverState,
    ] = useState<StatusPopoverState | null>(null)

    const {
        getVendorApartmentList,
        addVendorToPremium,
        removeVendorFromPremium,
        updateVendorStatus,
    } = useVendors()

    const {
        organizations: filterOrganizations,
        isLoading: organizationsLoading,
    } = useAdminFilterOrganizations(false)

    const organizations = useMemo(
        () =>
            [...filterOrganizations].sort((a, b) =>
                a.name.localeCompare(b.name),
            ),
        [filterOrganizations],
    )

    const filterParams = useMemo(
        () => ({
            search: debouncedSearch,
            state: stateFilter,
            organizationId,
            inHouse: typeFilter,
            status,
            premiumOnly,
            ordering,
        }),
        [
            debouncedSearch,
            stateFilter,
            organizationId,
            typeFilter,
            status,
            premiumOnly,
            ordering,
        ],
    )

    const listParams = useMemo(
        () => ({
            ...filterParams,
            page: page + 1,
            pageSize: rowsPerPage,
        }),
        [filterParams, page, rowsPerPage],
    )

    const { vendors, count, isLoading, isFetching } = useAdminVendorsList(
        listParams,
    )

    const stateSelectorValue = useMemo(() => {
        if (!stateFilter) {
            return -1
        }
        const index = statesList.indexOf(stateFilter)
        return index === -1 ? -1 : index
    }, [stateFilter])

    const typeSelectorValue = useMemo(() => {
        if (typeFilter === null) {
            return -1
        }
        return typeFilter ? 0 : 1
    }, [typeFilter])

    const statusSelectorValue = useMemo(() => {
        if (status === 'ALL') {
            return -1
        }
        const index = STATUS_SELECTOR_DATA.findIndex(
            (item) => item.status === status,
        )
        return index === -1 ? -1 : index
    }, [status])

    useEffect(() => {
        setPage(0)
    }, [
        debouncedSearch,
        stateFilter,
        organizationId,
        typeFilter,
        status,
        premiumOnly,
        ordering,
        rowsPerPage,
    ])

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const getSortDirectionForColumn = (
        columnId: string,
    ): 'asc' | 'desc' | null => {
        const base = VENDOR_SORT_KEY_TO_ORDERING[columnId] ?? columnId
        if (ordering === base) return 'asc'
        if (ordering === `-${base}`) return 'desc'
        return null
    }

    const handleSort = useCallback((key: string) => {
        setOrdering((prev) => toggleOrdering(prev, key))
    }, [])

    const handleViewApartments = async (
        event: React.MouseEvent,
        vendor: CompanyWithInvoiceCode,
    ) => {
        event.stopPropagation()
        setSelectedVendor(vendor)
        setModalOpen(true)
        setApartmentsLoading(true)

        try {
            const apartments = await getVendorApartmentList(vendor.id)
            setVendorApartments(apartments)
        } catch (error) {
            console.error('Error fetching vendor apartments:', error)
            setVendorApartments([])
        } finally {
            setApartmentsLoading(false)
        }
    }

    const handleCloseModal = () => {
        setModalOpen(false)
    }

    const openActionsMenu = (
        event: React.MouseEvent<HTMLElement>,
        vendor: CompanyWithInvoiceCode,
    ) => {
        event.stopPropagation()
        setMenuAnchor(event.currentTarget)
        setMenuVendor(vendor)
    }

    const closeActionsMenu = () => {
        setMenuAnchor(null)
        setMenuVendor(null)
    }

    const openStatusPopover = (
        e: React.MouseEvent,
        context: StatusPopoverContext,
    ) => {
        setStatusPopoverState({
            anchorPosition: { top: e.clientY, left: e.clientX },
            context,
        })
    }

    const handleStatusUpdate = async (id: number, newStatus: CompanyStatus) => {
        try {
            await updateVendorStatus(id, newStatus)
            toast.success('Vendor status updated successfully')
        } catch (err: any) {
            toast.error(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    'Error updating vendor status',
            )
        }
    }

    const handleAddToPremium = async () => {
        if (!menuVendor) return
        setActionsLoading(true)
        try {
            await addVendorToPremium(menuVendor.id)
            toast.success(`${menuVendor.name} added to premium plan.`)
            closeActionsMenu()
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Failed to add vendor to premium plan.',
            )
        } finally {
            setActionsLoading(false)
        }
    }

    const handleRemoveFromPremium = async () => {
        if (!menuVendor) return
        setActionsLoading(true)
        try {
            await removeVendorFromPremium(menuVendor.id)
            toast.success(`${menuVendor.name} removed from premium plan.`)
            closeActionsMenu()
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Failed to remove vendor from premium plan.',
            )
        } finally {
            setActionsLoading(false)
        }
    }

    const handleDownloadCsv = async () => {
        setIsExporting(true)
        try {
            const queryString = buildAdminVendorsFilterParams(
                filterParams,
            ).toString()
            const response = await axiosInstance.get(
                `/account-manager/vendors/admin-list/export/?${queryString}`,
                { responseType: 'blob' },
            )
            const blob = new Blob([response.data], {
                type: 'text/csv;charset=utf-8;',
            })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            const date = new Date().toISOString().split('T')[0]

            link.href = url
            link.download = `vendors-${date}.csv`
            link.click()
            URL.revokeObjectURL(url)
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Failed to export vendors. Please try again.',
            )
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className={localClasses.contentWrapper}>
            <VendorViewPills value={selectedView} onChange={setSelectedView} />

            {selectedView === 'analytics' && (
                <AdminVendorAnalytics organizations={organizations} />
            )}

            {selectedView === 'directory' && (
                <>
                    <div className={localClasses.searchContainer}>
                        <TextField
                            className={localClasses.searchField}
                            variant="outlined"
                            size="small"
                            placeholder="Search by vendor name or email..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Selector
                            label="Organization"
                            currentValue={organizationId ?? -1}
                            onChange={(e) => {
                                const nextId = Number(e.target.value)
                                setOrganizationId(nextId === -1 ? null : nextId)
                            }}
                            data={organizations}
                            getDisplayString={(org) => org.name}
                            searchable
                            noSelectionLabel="All organizations"
                            size="small"
                            variant="outlined"
                            customStyle={{
                                formControl: { minWidth: 220 },
                            }}
                            disabled={organizationsLoading}
                            maxItems={25}
                            popupHeight={420}
                        />
                        <Selector
                            label="State"
                            currentValue={stateSelectorValue}
                            onChange={(e) => {
                                const nextId = Number(e.target.value)
                                setStateFilter(
                                    nextId === -1 ? null : statesList[nextId],
                                )
                            }}
                            data={STATE_SELECTOR_DATA}
                            getDisplayString={(item) => item.name}
                            noSelectionLabel="All states"
                            size="small"
                            variant="outlined"
                            customStyle={{
                                formControl: { minWidth: 120 },
                            }}
                        />
                        <Selector
                            label="Type"
                            currentValue={typeSelectorValue}
                            onChange={(e) => {
                                const nextId = Number(e.target.value)
                                setTypeFilter(
                                    nextId === -1
                                        ? null
                                        : TYPE_SELECTOR_DATA[nextId].inHouse,
                                )
                            }}
                            data={TYPE_SELECTOR_DATA}
                            getDisplayString={(item) => item.name}
                            noSelectionLabel="All types"
                            size="small"
                            variant="outlined"
                            customStyle={{
                                formControl: { minWidth: 140 },
                            }}
                        />
                        <Selector
                            label="Status"
                            currentValue={statusSelectorValue}
                            onChange={(e) => {
                                const nextId = Number(e.target.value)
                                setStatus(
                                    nextId === -1
                                        ? 'ALL'
                                        : STATUS_SELECTOR_DATA[nextId].status!,
                                )
                            }}
                            data={STATUS_SELECTOR_DATA}
                            getDisplayString={(item) => item.name}
                            getOrnament={(item) =>
                                item.status ? (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            alignSelf: 'center',
                                            marginRight: theme.spacing(1),
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: getStatusDotColor(
                                                    item.status,
                                                    theme,
                                                ),
                                                flexShrink: 0,
                                            }}
                                        />
                                    </span>
                                ) : (
                                    <></>
                                )
                            }
                            customRenderValue={() => (
                                <StatusOptionLabel
                                    status={
                                        status === 'ALL' ? undefined : status
                                    }
                                    label={
                                        status === 'ALL'
                                            ? 'All statuses'
                                            : formatStatusLabel(status)
                                    }
                                />
                            )}
                            noSelectionLabel="All statuses"
                            size="small"
                            variant="outlined"
                            customStyle={{
                                formControl: { minWidth: 160 },
                                menuItem: { alignItems: 'center' },
                                select: {
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                text: { lineHeight: 1 },
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={premiumOnly}
                                    onChange={(e) =>
                                        setPremiumOnly(e.target.checked)
                                    }
                                    color="primary"
                                    size="small"
                                />
                            }
                            label="Premium only"
                        />
                        <Button
                            className={localClasses.downloadButton}
                            color="primary"
                            disabled={isExporting}
                            onClick={handleDownloadCsv}
                            startIcon={
                                isExporting ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                ) : (
                                    <GetAppIcon />
                                )
                            }
                            variant="contained"
                            size="small"
                        >
                            {isExporting ? 'Exporting…' : 'Download CSV'}
                        </Button>
                    </div>

                    <TableContainer
                        component={Paper}
                        className={localClasses.tableWrapper}
                    >
                        <Table
                            className={localClasses.table}
                            size="small"
                            stickyHeader
                        >
                            <TableHead>
                                <TableRow>
                                    {TABLE_HEAD_CELLS.map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            align={cell.align}
                                            padding={
                                                cell.disablePadding
                                                    ? 'none'
                                                    : 'default'
                                            }
                                            className={`${
                                                localClasses.stickyHeader
                                            } ${
                                                cell.id !== 'actions'
                                                    ? localClasses.sortableHeader
                                                    : ''
                                            }`}
                                            style={{ width: cell.width }}
                                            onClick={
                                                cell.id !== 'actions'
                                                    ? () => handleSort(cell.id)
                                                    : undefined
                                            }
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        cell.align === 'center'
                                                            ? 'center'
                                                            : 'flex-start',
                                                }}
                                            >
                                                {cell.label}
                                                {cell.id !== 'actions' &&
                                                    getSortDirectionForColumn(
                                                        cell.id,
                                                    ) === 'asc' && (
                                                        <ArrowUpwardIcon
                                                            className={
                                                                localClasses.sortIcon
                                                            }
                                                        />
                                                    )}
                                                {cell.id !== 'actions' &&
                                                    getSortDirectionForColumn(
                                                        cell.id,
                                                    ) === 'desc' && (
                                                        <ArrowDownwardIcon
                                                            className={
                                                                localClasses.sortIcon
                                                            }
                                                        />
                                                    )}
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vendors.map((vendor) => (
                                    <TableRow
                                        key={`ROW_${vendor.id}`}
                                        hover
                                        className={localClasses.tableRow}
                                        onClick={(e) => {
                                            openStatusPopover(e, {
                                                vendorId: vendor.id,
                                                currentStatus: vendor.status,
                                                propertyCount:
                                                    vendor.property_count,
                                            })
                                        }}
                                    >
                                        <TableCell>{vendor.id}</TableCell>
                                        <TableCell>
                                            <VendorNameCell
                                                name={vendor.name}
                                                organization={
                                                    vendor.organization.name
                                                }
                                                status={vendor.status}
                                                isPremium={
                                                    vendor.stripe_subscription
                                                        ?.plan ===
                                                    PlanStatus.PREMIUM
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {vendor.invoice_code}
                                        </TableCell>
                                        <TableCell>{vendor.email}</TableCell>
                                        <TableCell>
                                            {vendor.in_house
                                                ? 'In House'
                                                : 'Contractor'}
                                        </TableCell>
                                        <TableCell>
                                            {vendor.primary_address ? (
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .address1
                                                        }
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .city
                                                        }
                                                        ,{' '}
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .state
                                                        }{' '}
                                                        {
                                                            vendor
                                                                .primary_address
                                                                .zip_code
                                                        }
                                                    </Typography>
                                                </div>
                                            ) : (
                                                <Typography variant="subtitle2">
                                                    N/A
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {vendor.property_count !== null
                                                ? vendor.property_count
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            className={localClasses.actionsCell}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Tooltip title="View Associated Apartments">
                                                <IconButton
                                                    className={
                                                        localClasses.actionButton
                                                    }
                                                    onClick={(e) =>
                                                        handleViewApartments(
                                                            e,
                                                            vendor,
                                                        )
                                                    }
                                                    size="small"
                                                >
                                                    <BusinessIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="View actions">
                                                <IconButton
                                                    className={
                                                        localClasses.actionButton
                                                    }
                                                    onClick={(e) =>
                                                        openActionsMenu(
                                                            e,
                                                            vendor,
                                                        )
                                                    }
                                                    size="small"
                                                    aria-label="View actions"
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <div className={localClasses.footer}>
                        <TablePagination
                            count={count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </div>

                    {(isLoading || isFetching) && (
                        <div className={localClasses.loadingOverlay}>
                            <CircularProgress />
                        </div>
                    )}
                </>
            )}

            <VendorApartmentModal
                open={modalOpen}
                onClose={handleCloseModal}
                vendor={selectedVendor}
                apartments={vendorApartments}
                isLoading={apartmentsLoading}
            />

            <Menu
                anchorEl={menuAnchor}
                keepMounted
                open={Boolean(menuAnchor)}
                onClose={closeActionsMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {menuVendor?.stripe_subscription?.plan ===
                PlanStatus.PREMIUM ? (
                    <MenuItem
                        onClick={handleRemoveFromPremium}
                        disabled={actionsLoading}
                    >
                        Remove from Premium
                    </MenuItem>
                ) : (
                    <MenuItem
                        onClick={handleAddToPremium}
                        disabled={actionsLoading}
                    >
                        Add to Premium
                    </MenuItem>
                )}
            </Menu>

            <StatusChangePopover
                popoverState={statusPopoverState}
                onClose={() => setStatusPopoverState(null)}
                onStatusChange={handleStatusUpdate}
            />
        </div>
    )
}
